import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { formatDateTime } from '../utils/dateUtils';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const Visitors = () => {
  const [currentVisitors, setCurrentVisitors] = useState([]);
  const [visitorHistory, setVisitorHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState([]);
  const [locations, setLocations] = useState(null);
  const [devices, setDevices] = useState(null);
  const [trafficSources, setTrafficSources] = useState([]);
  const [peakTimes, setPeakTimes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trendPeriod, setTrendPeriod] = useState('week'); // day, week, month
  const [showHistory, setShowHistory] = useState(false);
  const [showJourneys, setShowJourneys] = useState(false);
  const [userJourneys, setUserJourneys] = useState([]);
  const [journeysTotal, setJourneysTotal] = useState(0);
  const [journeysPage, setJourneysPage] = useState(0);
  // Set default start date to 90 days ago to show all recent history
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 90);
    return date.toISOString().split('T')[0];
  });

  useEffect(() => {
    fetchVisitorData();

    // Refresh every 30 seconds
    const interval = setInterval(fetchVisitorData, 30000);
    return () => clearInterval(interval);
  }, [trendPeriod]);

  useEffect(() => {
    if (showHistory) {
      fetchVisitorHistory();
    }
  }, [showHistory, historyPage, startDate]);

  useEffect(() => {
    if (showJourneys) {
      fetchUserJourneys();
    }
  }, [showJourneys, journeysPage, startDate]);

  const fetchVisitorData = async () => {
    try {
      const [
        currentRes,
        statsRes,
        trendsRes,
        locationsRes,
        devicesRes,
        trafficRes,
        peakTimesRes
      ] = await Promise.all([
        api.get('/admin/visitors/current'),
        api.get('/admin/visitors/stats'),
        api.get(`/admin/visitors/analytics/trends?period=${trendPeriod}`),
        api.get('/admin/visitors/analytics/locations'),
        api.get('/admin/visitors/analytics/devices'),
        api.get('/admin/visitors/analytics/traffic-sources'),
        api.get('/admin/visitors/analytics/peak-times')
      ]);

      setCurrentVisitors(currentRes.data.visitors || []);
      setStats(statsRes.data.stats);
      setTrends(trendsRes.data.trends || []);
      setLocations(locationsRes.data.locations);
      setDevices(devicesRes.data.devices);
      setTrafficSources(trafficRes.data.trafficSources || []);
      setPeakTimes(peakTimesRes.data.peakTimes);
    } catch (err) {
      console.error('Failed to fetch visitor data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchVisitorHistory = async () => {
    try {
      const limit = 50;
      const offset = historyPage * limit;
      const response = await api.get(`/admin/visitors/history?limit=${limit}&offset=${offset}&startDate=${startDate}`);
      setVisitorHistory(response.data.visits || []);
      setHistoryTotal(response.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch visitor history:', err);
    }
  };

  const fetchUserJourneys = async () => {
    try {
      const limit = 20;
      const offset = journeysPage * limit;
      const response = await api.get(`/admin/visitors/journeys?limit=${limit}&offset=${offset}&startDate=${startDate}`);
      setUserJourneys(response.data.journeys || []);
      setJourneysTotal(response.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch user journeys:', err);
    }
  };

  const formatPageName = (url) => {
    if (!url) return 'Unknown';
    // Extract path from full URL
    const path = url.replace(/^https?:\/\/[^/]+/, '');
    // Remove query parameters
    const cleanPath = path.split('?')[0];
    // Convert to readable name
    const pageNames = {
      '/': 'Home',
      '/index.html': 'Home',
      '/about': 'About Us',
      '/about.html': 'About Us',
      '/services': 'What is Electrolysis?',
      '/services.html': 'What is Electrolysis?',
      '/pricing': 'Pricing',
      '/pricing.html': 'Pricing',
      '/contact': 'Contact Us',
      '/contact.html': 'Contact Us',
      '/booking': 'Book a Session',
      '/booking.html': 'Book a Session'
    };
    return pageNames[cleanPath] || cleanPath;
  };

  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-3xl font-bold text-primary mb-6">Visitor Analytics</h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Active Now</p>
              <p className="text-3xl font-bold text-green-600 mt-2 flex items-center gap-2">
                <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
                {currentVisitors.length}
              </p>
            </div>
            <div className="bg-green-100 text-green-600 p-3 rounded-full">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Today</p>
              <p className="text-3xl font-bold text-primary mt-2">
                {stats?.todayVisitors || 0}
              </p>
            </div>
            <div className="bg-blue-100 text-blue-600 p-3 rounded-full">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Page Views (Today)</p>
              <p className="text-3xl font-bold text-primary mt-2">
                {stats?.todayPageViews || 0}
              </p>
            </div>
            <div className="bg-purple-100 text-purple-600 p-3 rounded-full">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Avg. Session</p>
              <p className="text-3xl font-bold text-primary mt-2">
                {stats?.todayPageViews && stats?.todayVisitors
                  ? (stats.todayPageViews / stats.todayVisitors).toFixed(1)
                  : '0'}
              </p>
              <p className="text-xs text-gray-500">pages/visitor</p>
            </div>
            <div className="bg-yellow-100 text-yellow-600 p-3 rounded-full">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Visitor Trends Chart */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Visitor Trends</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setTrendPeriod('day')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                trendPeriod === 'day'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              24 Hours
            </button>
            <button
              onClick={() => setTrendPeriod('week')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                trendPeriod === 'week'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              7 Days
            </button>
            <button
              onClick={() => setTrendPeriod('month')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                trendPeriod === 'month'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              30 Days
            </button>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={trends}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="period" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="visitors" stroke="#3B82F6" strokeWidth={2} name="Visitors" />
            <Line type="monotone" dataKey="pageViews" stroke="#10B981" strokeWidth={2} name="Page Views" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Device Types & Traffic Sources */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Device Types */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Device Types</h2>
          {devices?.deviceTypes && devices.deviceTypes.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={devices.deviceTypes}
                  dataKey="visitors"
                  nameKey="type"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={(entry) => `${entry.type}: ${entry.visitors}`}
                >
                  {devices.deviceTypes.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-500 text-center py-8">No device data available</p>
          )}
        </div>

        {/* Traffic Sources */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Traffic Sources</h2>
          {trafficSources.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={trafficSources}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="source" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="visitors" fill="#3B82F6" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-500 text-center py-8">No traffic source data available</p>
          )}
        </div>
      </div>

      {/* Geographic Locations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Top Countries */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Top Countries</h2>
          {locations?.countries && locations.countries.length > 0 ? (
            <div className="space-y-3">
              {locations.countries.map((location, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="font-medium text-gray-900">{location.country}</span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                    {location.visitors} visitors
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No location data available yet</p>
          )}
        </div>

        {/* Top Cities */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Top Cities</h2>
          {locations?.cities && locations.cities.length > 0 ? (
            <div className="space-y-3">
              {locations.cities.map((location, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{location.city}</p>
                    <p className="text-xs text-gray-500">{location.region}, {location.country}</p>
                  </div>
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                    {location.visitors}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No city data available yet</p>
          )}
        </div>
      </div>

      {/* Peak Times */}
      {peakTimes && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Peak Hours */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Peak Hours (Last 7 Days)</h2>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={peakTimes.hourly}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" label={{ value: 'Hour of Day', position: 'insideBottom', offset: -5 }} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="visitors" fill="#8B5CF6" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Day of Week */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Day of Week (Last 30 Days)</h2>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={peakTimes.dayOfWeek}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="visitors" fill="#EC4899" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Browsers & OS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Top Browsers */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Top Browsers</h2>
          {devices?.browsers && devices.browsers.length > 0 ? (
            <div className="space-y-3">
              {devices.browsers.map((browser, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="font-medium text-gray-900">{browser.browser}</span>
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                    {browser.visitors}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No browser data available</p>
          )}
        </div>

        {/* Top Operating Systems */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Operating Systems</h2>
          {devices?.operatingSystems && devices.operatingSystems.length > 0 ? (
            <div className="space-y-3">
              {devices.operatingSystems.map((os, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="font-medium text-gray-900">{os.os}</span>
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                    {os.visitors}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No OS data available</p>
          )}
        </div>
      </div>

      {/* Most Visited Pages */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Most Visited Pages (Today)</h2>
        {stats?.topPages && stats.topPages.length > 0 ? (
          <div className="space-y-3">
            {stats.topPages.map((page, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-semibold">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {page.page_url}
                    </p>
                  </div>
                </div>
                <div className="flex-shrink-0 ml-4">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">
                    {page.views} views
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No page view data available yet</p>
        )}
      </div>

      {/* Current Active Visitors */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
          Active Visitors ({currentVisitors.length})
        </h2>
        {currentVisitors.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    IP Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Current Page
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Device
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Browser
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Last Seen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentVisitors.map((visitor, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                      {visitor.ipAddress || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {visitor.city && visitor.region && visitor.country
                        ? `${visitor.city}, ${visitor.region}, ${visitor.country}`
                        : visitor.city && visitor.country
                        ? `${visitor.city}, ${visitor.country}`
                        : visitor.country || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="max-w-xs truncate">
                        {visitor.currentPage || '/'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                        {visitor.deviceType || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {visitor.browser || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDateTime(visitor.lastSeen, 'h:mm a')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No active visitors right now</p>
        )}
      </div>

      {/* Visitor History Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Visitor History</h2>
          <div className="flex items-center gap-4">
            {showHistory && (
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">From:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setHistoryPage(0);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            )}
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              {showHistory ? 'Hide History' : 'Show History'}
            </button>
          </div>
        </div>

        {showHistory && (
          <>
            {visitorHistory.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Date & Time
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          IP Address
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Location
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Page
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Device
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Browser
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          OS
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Source
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {visitorHistory.map((visit) => (
                        <tr key={visit.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDateTime(visit.visitedAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                            {visit.ipAddress || 'Unknown'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {visit.isNewVisitor ? (
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                                New
                              </span>
                            ) : (
                              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                                Returning
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {visit.isBot ? (
                              <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs font-semibold">
                                🤖 Bot
                              </span>
                            ) : (
                              <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-semibold">
                                👤 Human
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {visit.city && visit.region && visit.country
                              ? `${visit.city}, ${visit.region}, ${visit.country}`
                              : visit.city && visit.country
                              ? `${visit.city}, ${visit.country}`
                              : visit.country || 'Unknown'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            <div className="max-w-xs truncate" title={visit.page}>
                              {visit.page}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                              {visit.deviceType || 'Unknown'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {visit.browser || 'Unknown'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {visit.os || 'Unknown'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className={`px-2 py-1 rounded text-xs ${
                              visit.trafficSource === 'direct' ? 'bg-blue-100 text-blue-800' :
                              visit.trafficSource === 'search' ? 'bg-green-100 text-green-800' :
                              visit.trafficSource === 'social' ? 'bg-purple-100 text-purple-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {visit.trafficSource || 'unknown'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="mt-6 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {historyPage * 50 + 1} to {Math.min((historyPage + 1) * 50, historyTotal)} of {historyTotal} visits
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setHistoryPage(Math.max(0, historyPage - 1))}
                      disabled={historyPage === 0}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setHistoryPage(historyPage + 1)}
                      disabled={(historyPage + 1) * 50 >= historyTotal}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-gray-500 text-center py-8">No visitor history available</p>
            )}
          </>
        )}
      </div>

      {/* User Journeys Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mt-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">User Journeys</h2>
          <button
            onClick={() => {
              setShowJourneys(!showJourneys);
              setJourneysPage(0);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            {showJourneys ? 'Hide Journeys' : 'Show Journeys'}
          </button>
        </div>

        {showJourneys && (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                From Date:
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setJourneysPage(0);
                }}
                className="border border-gray-300 rounded-md px-3 py-2"
              />
            </div>

            {userJourneys.length > 0 ? (
              <>
                <div className="space-y-4 mb-6">
                  {userJourneys.map((journey) => (
                    <div key={journey.sessionId} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      {/* Journey Header */}
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <span className="font-mono text-sm text-gray-900">{journey.ipAddress || 'Unknown'}</span>
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                              {journey.deviceType || 'unknown'}
                            </span>
                            <span className="text-sm text-gray-600">
                              {journey.city && journey.region && journey.country
                                ? `${journey.city}, ${journey.region}, ${journey.country}`
                                : journey.city && journey.country
                                ? `${journey.city}, ${journey.country}`
                                : journey.country || 'Unknown location'}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">
                            Session: {formatDateTime(journey.sessionStart)} • Duration: {formatDuration(journey.duration)} • {journey.pageViews} page{journey.pageViews !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>

                      {/* Page Path Flow */}
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          {journey.pagePath.map((page, index) => (
                            <React.Fragment key={index}>
                              <div className="flex items-center">
                                <span className="px-3 py-1.5 bg-blue-100 text-blue-800 rounded-md text-sm font-medium">
                                  {formatPageName(page)}
                                </span>
                              </div>
                              {index < journey.pagePath.length - 1 && (
                                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>

                      {/* Additional Info */}
                      <div className="mt-3 flex gap-4 text-xs text-gray-600">
                        <span>Browser: {journey.browser || 'Unknown'}</span>
                        <span>OS: {journey.os || 'Unknown'}</span>
                        <span>Source: {journey.trafficSource || 'Unknown'}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                <div className="flex justify-between items-center border-t pt-4">
                  <div className="text-sm text-gray-600">
                    Showing {journeysPage * 20 + 1} to {Math.min((journeysPage + 1) * 20, journeysTotal)} of {journeysTotal} sessions
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setJourneysPage(journeysPage - 1)}
                      disabled={journeysPage === 0}
                      className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setJourneysPage(journeysPage + 1)}
                      disabled={(journeysPage + 1) * 20 >= journeysTotal}
                      className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-gray-500 text-center py-8">No user journeys available</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Visitors;
